import { supabase } from '../config/supabase';

export interface MuxUploadResponse {
  id: string;
  playback_id: string;
  status: string;
}

export const createMuxUpload = async (file: File, title?: string, creatorId?: string): Promise<MuxUploadResponse> => {
  // Call our Edge Function to get the upload URL
  const { data: uploadData, error: uploadError } = await supabase.functions.invoke('mux-upload', {
    body: { title, creatorId }
  });

  if (uploadError) {
    throw new Error(`Failed to create Mux upload URL: ${uploadError.message}`);
  }

  if (!uploadData?.data?.url) {
    throw new Error('Invalid response from Edge Function - missing upload URL');
  }

  // Upload the file to the provided URL
  const uploadResponse = await fetch(uploadData.data.url, {
    method: 'PUT',
    body: file,
    headers: {
      'Content-Type': file.type,
    },
  });

  if (!uploadResponse.ok) {
    throw new Error('Failed to upload video to Mux');
  }

  // Wait for the asset to be created and ready
  let assetData;
  let attempts = 0;
  const maxAttempts = 10;
  const delay = 2000;

  while (attempts < maxAttempts) {
    // Get the asset details using the upload ID to find the asset
    const { data: assetResponse, error: assetError } = await supabase.functions.invoke('mux-upload-status', {
      body: { uploadId: uploadData.data.id }
    });

    if (assetError) {
      throw new Error('Failed to get Mux asset details');
    }
    
    if (assetResponse?.data?.asset_id) {
      // Now fetch the actual asset details
      const { data: detailsResponse, error: detailsError } = await supabase.functions.invoke('mux-asset-details', {
        body: { assetId: assetResponse.data.asset_id }
      });

      if (detailsError) {
        throw new Error('Failed to get Mux asset details');
      }

      assetData = detailsResponse.data;

      if (assetData.status === 'ready') {
        break;
      }
    }

    attempts++;
    if (attempts < maxAttempts) {
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }

  if (!assetData || assetData.status !== 'ready') {
    throw new Error('Asset not ready after maximum attempts');
  }

  return {
    id: assetData.id,
    playback_id: assetData.playback_ids[0].id,
    status: assetData.status,
  };
}; 